// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Card, CardContent, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  getSuitabilityProfileMetadata,
  type SuitabilityProfileType,
} from '@shared/advisory/account-workflow/utils';

export interface SuitabilityTestResultProps {
  riskProfile: string;
}

export const SuitabilityTestResult = defineComponent({
  displayName: 'SuitabilityTestResult',
  useSetup: ({ riskProfile }: SuitabilityTestResultProps) => {
    const validProfileTypes: SuitabilityProfileType[] = [
      'conservative',
      'moderate',
      'dynamic',
      'aggressive',
    ];
    const profileType = validProfileTypes.includes(
      riskProfile as SuitabilityProfileType,
    )
      ? (riskProfile as SuitabilityProfileType)
      : 'conservative';
    const metadata = getSuitabilityProfileMetadata(profileType);

    return {
      profileType,
      metadata,
    };
  },
  View: ({ profileType, metadata }) => {
    const getIconBgColor = () => {
      switch (profileType) {
        case 'conservative':
          return 'bg-finance-100';
        case 'moderate':
          return 'bg-suitability-moderate-bg';
        case 'dynamic':
          return 'bg-suitability-dynamic-bg';
        case 'aggressive':
          return 'bg-suitability-aggressive-bg';
        default:
          return 'bg-finance-100';
      }
    };

    return (
      <Card
        variant="outlined"
        className={`!rounded-lg !border-2 ${metadata.borderColor}`}
        data-testid={`suitability-test-result-${profileType}`}
      >
        <CardContent className="!flex !flex-col !gap-4 !p-7">
          <Typography
            variant="h4"
            className="!mb-2"
            data-testid="suitability-test-title"
          >
            <Translation
              i18nKey="onboarding-process-overview.result-and-signature.suitability-test.title"
              ns="components"
            />
          </Typography>

          <div className="flex items-start gap-4">
            <div
              className={`flex shrink-0 items-center justify-center rounded-lg ${getIconBgColor()} p-3`}
              data-testid="suitability-test-icon-container"
            >
              <AppIcon
                icon={metadata.icon}
                color={profileType === 'dynamic' ? undefined : metadata.iconColor}
                size={24}
                className={profileType === 'dynamic' ? '!text-pending' : undefined}
                data-testid="suitability-test-icon"
              />
            </div>

            <div
              className="flex flex-1 flex-col items-start gap-2"
              data-testid="suitability-test-profile-info"
            >
              <Typography
                variant="h3"
                color="black"
                data-testid="suitability-test-profile-title"
              >
                <Translation i18nKey={metadata.titleKey as any} ns="components" />
              </Typography>
              <Typography
                className="!text-muted-foreground"
                data-testid="suitability-test-profile-description"
              >
                <Translation
                  i18nKey={metadata.descriptionKey as any}
                  ns="components"
                />
              </Typography>

              <div
                className={`mt-1 flex flex-col gap-1 rounded-lg border p-3 ${
                  profileType === 'conservative'
                    ? 'border-primary/30 bg-finance-50'
                    : profileType === 'moderate'
                    ? 'border-positive/30 bg-suitability-moderate-light'
                    : profileType === 'dynamic'
                    ? 'border-pending/30 bg-suitability-dynamic-light'
                    : 'border-negative/30 bg-suitability-aggressive-light'
                }`}
                data-testid="suitability-test-volatility-box"
              >
                <Typography
                  className="!text-muted-foreground !text-sm"
                  data-testid="suitability-test-volatility-label"
                >
                  <Translation
                    i18nKey={metadata.volatilityLabelKey as any}
                    ns="components"
                  />
                </Typography>
                <Typography
                  className={`!font-semibold ${
                    profileType === 'conservative'
                      ? '!text-primary'
                      : profileType === 'moderate'
                      ? '!text-positive'
                      : profileType === 'dynamic'
                      ? '!text-pending'
                      : '!text-negative'
                  }`}
                  data-testid="suitability-test-volatility-value"
                >
                  <Translation
                    i18nKey={metadata.volatilityValueKey as any}
                    ns="components"
                  />
                </Typography>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  },
});
